import {
  LocaleSchema,
  type Locale,
  type LocalizedText,
} from "@sirdab/returns-shared";

/**
 * Locale and direction resolution. Pure — no DOM, no React — so it is testable and so the
 * same rules apply in a component, in a loader and in a test.
 *
 * Arabic is the DEFAULT, not a fallback. `index.html` ships `lang="ar" dir="rtl"`; anything
 * here that fails to resolve lands back on Arabic rather than English.
 */

export const DEFAULT_LOCALE: Locale = "ar";

export type Direction = "rtl" | "ltr";

/** Direction is a property of the locale, never of a component. */
export function directionFor(locale: Locale): Direction {
  return locale === "ar" ? "rtl" : "ltr";
}

export function otherLocale(locale: Locale): Locale {
  return locale === "ar" ? "en" : "ar";
}

/** Narrow an unknown value (query string, policy field, stored preference) to a Locale. */
export function parseLocale(value: unknown): Locale | null {
  const parsed = LocaleSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export interface LocaleSources {
  /** `?lang=` on the URL. Highest precedence: it is an explicit act by the shopper. */
  query?: string | null | undefined;
  /** What the shopper last chose in this tab. Session-scoped only (AGENTS.md). */
  session?: string | null | undefined;
  /** `policy.localeDefault` — the merchant's preference for their own storefront. */
  merchant?: string | null | undefined;
  /** `navigator.language`, only consulted when it is unambiguously Arabic or English. */
  navigator?: string | null | undefined;
}

/**
 * Precedence: explicit URL choice > this session's choice > merchant default > browser
 * language > Arabic. Every step is allowed to fail; the chain simply continues.
 */
export function resolveLocale(sources: LocaleSources = {}): Locale {
  return (
    parseLocale(sources.query) ??
    parseLocale(sources.session) ??
    parseLocale(sources.merchant) ??
    parseNavigatorLanguage(sources.navigator) ??
    DEFAULT_LOCALE
  );
}

/** `ar-SA` -> `ar`, `en-GB` -> `en`, anything else -> null (so the chain falls through). */
export function parseNavigatorLanguage(value: unknown): Locale | null {
  if (typeof value !== "string") return null;
  const primary = value.toLowerCase().split("-")[0] ?? "";
  return parseLocale(primary);
}

/** Pick the active side of a bilingual pair from the API or the catalogue. Never translates. */
export function pick(text: LocalizedText, locale: Locale): string {
  return locale === "ar" ? text.ar : text.en;
}

/** Session-scoped locale preference. Order data never goes here — only this one enum. */
const SESSION_LOCALE_KEY = "sirdab.locale";

export function readSessionLocale(
  storage?: Storage | undefined,
): Locale | null {
  try {
    const store = storage ?? globalThis.sessionStorage;
    return parseLocale(store?.getItem(SESSION_LOCALE_KEY));
  } catch {
    return null;
  }
}

export function writeSessionLocale(
  locale: Locale,
  storage?: Storage | undefined,
): void {
  try {
    const store = storage ?? globalThis.sessionStorage;
    store?.setItem(SESSION_LOCALE_KEY, locale);
  } catch {
    /* Private mode / blocked storage is not an error worth surfacing to a shopper. */
  }
}
